import { forwardRef, useState, useRef, useCallback, useEffect } from "react";
import { cn } from "../lib/utils";
import { Input } from "./input";
import { Button } from "./button";
import { ColorSelector } from "./color-selector";
import { Trash2, ChevronsUpDown, Droplets } from "lucide-react";
import { HexColorPicker } from "react-colorful";

export interface ColorSelectorMenuProps extends React.ComponentProps<"div"> {
  variant?: "default" | "custom";
  title?: string;
  colors?: string[];
  onColorSelect?: (color: string) => void;
  onColorAdd?: (color: string) => void;
  onTitleChange?: (title: string) => void;
  onVariantChange?: (variant: "default" | "custom") => void;
  onDelete?: () => void;
  onSave?: (data: { color: string; title: string }) => void;
}

const defaultColors = [
  "#FF6B6B",
  "#4ECDC4",
  "#45B7D1",
  "#96CEB4",
  "#FFEAA7",
  "#DDA0DD",
  "#FFB347",
  "#98D8C8",
  "#F7DC6F",
  "#BB8FCE",
  "#85C1E9",
  "#F8C471",
];

// Utility functions for color conversion
function normalizeColor(color: string): string {
  return color.toUpperCase();
}

function hexToHsv(hex: string): { h: number; s: number; v: number } {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const diff = max - min;

  let h = 0;
  if (diff !== 0) {
    if (max === r) h = ((g - b) / diff) % 6;
    else if (max === g) h = (b - r) / diff + 2;
    else h = (r - g) / diff + 4;
  }
  h = Math.round(h * 60);
  if (h < 0) h += 360;

  const s = max === 0 ? 0 : diff / max;
  const v = max;

  return { h, s: s * 100, v: v * 100 };
}

function hsvToHex(h: number, s: number, v: number): string {
  const sDecimal = s / 100;
  const vDecimal = v / 100;

  const c = vDecimal * sDecimal;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = vDecimal - c;

  let r = 0,
    g = 0,
    b = 0;

  if (0 <= h && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (60 <= h && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (120 <= h && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (180 <= h && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (240 <= h && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (300 <= h && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);

  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

function hsvToRgb(h: number, s: number, v: number): { r: number; g: number; b: number } {
  const sDecimal = s / 100;
  const vDecimal = v / 100;

  const c = vDecimal * sDecimal;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = vDecimal - c;

  let r = 0,
    g = 0,
    b = 0;

  if (0 <= h && h < 60) {
    r = c;
    g = x;
    b = 0;
  } else if (60 <= h && h < 120) {
    r = x;
    g = c;
    b = 0;
  } else if (120 <= h && h < 180) {
    r = 0;
    g = c;
    b = x;
  } else if (180 <= h && h < 240) {
    r = 0;
    g = x;
    b = c;
  } else if (240 <= h && h < 300) {
    r = x;
    g = 0;
    b = c;
  } else if (300 <= h && h < 360) {
    r = c;
    g = 0;
    b = x;
  }

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
}

const ColorSelectorMenu = forwardRef<HTMLDivElement, ColorSelectorMenuProps>(
  (
    {
      className,
      variant = "default",
      title = "Badge title",
      colors = defaultColors,
      onColorSelect,
      onColorAdd,
      onTitleChange,
      onVariantChange,
      onDelete,
      onSave,
      ...props
    },
    ref
  ) => {
    const [internalVariant, setInternalVariant] = useState(variant || "default");
    const [customColor, setCustomColor] = useState("#FF6B6B");
    const [hsv, setHsv] = useState(() => hexToHsv("#FF6B6B"));
    const [inputMode, setInputMode] = useState<"hex" | "hsl" | "rgb">("hex");
    const [internalColors, setInternalColors] = useState(colors || defaultColors);
    const [editingTitle, setEditingTitle] = useState(title);
    const sliderRef = useRef<HTMLDivElement>(null);
    const isDragging = useRef(false);

    const currentVariant = variant || internalVariant;
    const currentColors = internalColors;

    // Check if current color is in the preset colors
    const isCustomColorSelected = !currentColors.some((color) => normalizeColor(color) === normalizeColor(customColor));

    const updateColorFromHsv = useCallback((newHsv: { h: number; s: number; v: number }) => {
      setHsv(newHsv);
      const newHex = hsvToHex(newHsv.h, newHsv.s, newHsv.v);
      setCustomColor(newHex);
    }, []);

    const handleHexColorPickerChange = useCallback((color: string) => {
      setCustomColor(color);
      setHsv(hexToHsv(color));
    }, []);

    const handleSliderMouseDown = useCallback(
      (e: React.MouseEvent) => {
        if (!sliderRef.current) return;

        isDragging.current = true;
        const rect = sliderRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const hue = Math.max(0, Math.min(360, (x / rect.width) * 360));

        updateColorFromHsv({ ...hsv, h: hue });
      },
      [hsv, updateColorFromHsv]
    );

    const handleSliderMouseMove = useCallback(
      (e: MouseEvent) => {
        if (!isDragging.current || !sliderRef.current) return;

        const rect = sliderRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const hue = Math.max(0, Math.min(360, (x / rect.width) * 360));

        updateColorFromHsv({ ...hsv, h: hue });
      },
      [hsv, updateColorFromHsv]
    );

    const handleSliderMouseUp = useCallback(() => {
      isDragging.current = false;
    }, []);

    const handleSaveColor = useCallback(() => {
      if (isCustomColorSelected) {
        const newColors = [...currentColors, customColor];
        setInternalColors(newColors);
        onColorAdd?.(customColor);
      }
      // Call onSave with current color and title
      onSave?.({ color: customColor, title: editingTitle });
    }, [isCustomColorSelected, customColor, currentColors, onColorAdd, onSave, editingTitle]);

    const handleDeleteColor = useCallback(() => {
      const colorExists = currentColors.some((color) => normalizeColor(color) === normalizeColor(customColor));
      if (colorExists) {
        const newColors = currentColors.filter((color) => normalizeColor(color) !== normalizeColor(customColor));
        setInternalColors(newColors);
      }
      onDelete?.();
    }, [customColor, currentColors, onDelete]);

    // Sync internal colors with colors prop
    useEffect(() => {
      if (colors) {
        setInternalColors(colors);
      }
    }, [colors]);

    // Sync editing title with title prop when the component is not focused
    const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);
    useEffect(() => {
      // Only sync with title prop if the input is not currently focused
      if (!inputRef || document.activeElement !== inputRef) {
        setEditingTitle(title);
      }
    }, [title, inputRef]);

    // Handle title input key events
    const handleTitleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && editingTitle.trim() && editingTitle !== title) {
          onTitleChange?.(editingTitle.trim());
        } else if (e.key === "Escape") {
          setEditingTitle(title); // Reset to original title
        }
      },
      [editingTitle, title, onTitleChange]
    );

    // Add global mouse event listeners for slider dragging
    useEffect(() => {
      if (isDragging.current) {
        document.addEventListener("mousemove", handleSliderMouseMove);
        document.addEventListener("mouseup", handleSliderMouseUp);

        return () => {
          document.removeEventListener("mousemove", handleSliderMouseMove);
          document.removeEventListener("mouseup", handleSliderMouseUp);
        };
      }
    }, [handleSliderMouseMove, handleSliderMouseUp]);

    return (
      <div
        ref={ref}
        className={cn("bg-popover border border-border rounded-md w-[250px] flex flex-col", className)}
        {...props}
      >
        {/* Title Input */}
        <div className="p-1">
          <Input
            ref={setInputRef}
            value={editingTitle}
            onChange={(e) => setEditingTitle(e.target.value)}
            onKeyDown={handleTitleKeyDown}
            className="h-9 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-input focus:border-input"
            placeholder="Badge title"
          />
        </div>

        {/* Custom Color Picker (only shown when variant is custom) */}
        {currentVariant === "custom" && (
          <div className="px-1 pb-1">
            <div className="bg-white rounded-lg h-[102px] mb-1 relative overflow-hidden">
              <HexColorPicker
                color={customColor}
                onChange={handleHexColorPickerChange}
                style={
                  {
                    width: "100%",
                    height: "100%",
                    "--react-colorful-hue-pointer-size": "0px",
                    "--react-colorful-saturation-pointer-size": "12px",
                  } as React.CSSProperties
                }
              />
              <style
                // eslint-disable-next-line @tellescope/no-unsanitized-inner-html -- We're just setting styles here
                dangerouslySetInnerHTML={{
                  __html: `
                  .react-colorful__hue {
                    display: none !important;
                  }
                `,
                }}
              />
            </div>

            {/* Hue slider */}
            <div className="flex items-center gap-2">
              <div
                ref={sliderRef}
                className="flex-1 h-1.5 rounded-full relative cursor-pointer"
                style={{
                  background:
                    "linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)",
                }}
                onMouseDown={handleSliderMouseDown}
              >
                <div
                  className="absolute w-1.5 h-1.5 border border-white rounded-full top-1/2 transform -translate-y-1/2 pointer-events-none"
                  style={{
                    left: `${(hsv.h / 360) * 100}%`,
                    transform: "translate(-50%, -50%)",
                    backgroundColor: "transparent",
                  }}
                />
              </div>
              <div className="w-9 h-9 rounded-full shadow-sm" style={{ backgroundColor: customColor }} />
            </div>

            {/* Color input - Hex or HSL */}
            <div className="mb-2">
              {inputMode === "hex" && (
                <>
                  <label className="text-sm font-medium text-foreground block mb-2">Hex</label>
                  <div className="flex gap-2">
                    <Input
                      value={customColor}
                      onChange={(e) => {
                        const newColor = e.target.value;
                        setCustomColor(newColor);
                        if (newColor.match(/^#[0-9A-Fa-f]{6}$/)) {
                          setHsv(hexToHsv(newColor));
                        }
                      }}
                      placeholder="#FFFFFF"
                      className="h-9 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-input focus:border-input"
                    />
                    <Button variant="secondary" className="h-9 w-9 p-0" onClick={() => setInputMode("hsl")}>
                      <ChevronsUpDown className="size-3.5" />
                    </Button>
                  </div>
                </>
              )}
              {inputMode === "hsl" && (
                <>
                  <div className="flex gap-2 mb-2">
                    <div className="flex-1">
                      <label className="text-sm font-medium text-foreground block">H</label>
                    </div>
                    <div className="flex-1">
                      <label className="text-sm font-medium text-foreground block">S</label>
                    </div>
                    <div className="flex-1">
                      <label className="text-sm font-medium text-foreground block">L</label>
                    </div>
                    <div className="w-9"></div> {/* Spacer for button alignment */}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={Math.round(hsv.h)}
                      onChange={(e) => {
                        const h = parseInt(e.target.value) || 0;
                        updateColorFromHsv({ ...hsv, h: Math.max(0, Math.min(360, h)) });
                      }}
                      placeholder="0"
                      className="h-9 flex-1 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-input focus:border-input"
                    />
                    <Input
                      value={Math.round(hsv.s)}
                      onChange={(e) => {
                        const s = parseInt(e.target.value) || 0;
                        updateColorFromHsv({ ...hsv, s: Math.max(0, Math.min(100, s)) });
                      }}
                      placeholder="0"
                      className="h-9 flex-1 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-input focus:border-input"
                    />
                    <Input
                      value={Math.round(hsv.v)}
                      onChange={(e) => {
                        const v = parseInt(e.target.value) || 0;
                        updateColorFromHsv({ ...hsv, v: Math.max(0, Math.min(100, v)) });
                      }}
                      placeholder="0"
                      className="h-9 flex-1 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-input focus:border-input"
                    />
                    <Button
                      variant="secondary"
                      className="h-9 w-9 p-0 flex-shrink-0"
                      onClick={() => setInputMode("rgb")}
                    >
                      <ChevronsUpDown className="size-3.5" />
                    </Button>
                  </div>
                </>
              )}
              {inputMode === "rgb" && (
                <>
                  <div className="flex gap-2 mb-2">
                    <div className="flex-1">
                      <label className="text-sm font-medium text-foreground block">R</label>
                    </div>
                    <div className="flex-1">
                      <label className="text-sm font-medium text-foreground block">G</label>
                    </div>
                    <div className="flex-1">
                      <label className="text-sm font-medium text-foreground block">B</label>
                    </div>
                    <div className="w-9"></div> {/* Spacer for button alignment */}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={hsvToRgb(hsv.h, hsv.s, hsv.v).r}
                      onChange={(e) => {
                        const r = parseInt(e.target.value) || 0;
                        const { g, b } = hsvToRgb(hsv.h, hsv.s, hsv.v);
                        const newHex = `#${Math.max(0, Math.min(255, r)).toString(16).padStart(2, "0")}${g
                          .toString(16)
                          .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
                        setCustomColor(newHex);
                        setHsv(hexToHsv(newHex));
                      }}
                      placeholder="0"
                      className="h-9 flex-1 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-input focus:border-input"
                    />
                    <Input
                      value={hsvToRgb(hsv.h, hsv.s, hsv.v).g}
                      onChange={(e) => {
                        const g = parseInt(e.target.value) || 0;
                        const { r, b } = hsvToRgb(hsv.h, hsv.s, hsv.v);
                        const newHex = `#${r.toString(16).padStart(2, "0")}${Math.max(0, Math.min(255, g))
                          .toString(16)
                          .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
                        setCustomColor(newHex);
                        setHsv(hexToHsv(newHex));
                      }}
                      placeholder="0"
                      className="h-9 flex-1 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-input focus:border-input"
                    />
                    <Input
                      value={hsvToRgb(hsv.h, hsv.s, hsv.v).b}
                      onChange={(e) => {
                        const b = parseInt(e.target.value) || 0;
                        const { r, g } = hsvToRgb(hsv.h, hsv.s, hsv.v);
                        const newHex = `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${Math.max(
                          0,
                          Math.min(255, b)
                        )
                          .toString(16)
                          .padStart(2, "0")}`;
                        setCustomColor(newHex);
                        setHsv(hexToHsv(newHex));
                      }}
                      placeholder="0"
                      className="h-9 flex-1 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-input focus:border-input"
                    />
                    <Button
                      variant="secondary"
                      className="h-9 w-9 p-0 flex-shrink-0"
                      onClick={() => setInputMode("hex")}
                    >
                      <ChevronsUpDown className="size-3.5" />
                    </Button>
                  </div>
                </>
              )}
            </div>

            {/* Save button */}
            <Button className="w-full" onClick={handleSaveColor}>
              Save
            </Button>
          </div>
        )}

        {/* Color Grid */}
        <div className="p-1">
          <div className="bg-white p-1">
            <div>
              {(() => {
                const colorsPerRow = 7; // Consistent 7 colors per row
                const rows = [];

                // Create rows of colors with consistent spacing
                for (let i = 0; i < currentColors.length; i += colorsPerRow) {
                  const rowColors = currentColors.slice(i, i + colorsPerRow);

                  // Use consistent gap spacing for all rows with fixed height matching button rows
                  const isFirstRow = i === 0;
                  rows.push(
                    <div
                      key={`row-${i / colorsPerRow}`}
                      className={`flex gap-[20px] flex-shrink-0 items-center h-7 ${!isFirstRow ? "mt-2" : ""}`}
                    >
                      {rowColors.map((color, index) => (
                        <ColorSelector
                          key={i + index}
                          color={color}
                          onClick={() => {
                            onColorSelect?.(color);
                            setCustomColor(color);
                            setHsv(hexToHsv(color));
                          }}
                          className="flex-shrink-0"
                        />
                      ))}
                    </div>
                  );
                }

                // Add droplets button row
                const lastRowColorCount = currentColors.length % colorsPerRow;
                const shouldStartNewRow = lastRowColorCount === 0 && currentColors.length > 0;

                if (shouldStartNewRow) {
                  // Start new row with just droplets button, aligned with first color above
                  rows.push(
                    <div key="droplets-row" className="flex flex-shrink-0 items-center h-7 mt-2">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => {
                          if (currentVariant === "default") {
                            const newVariant = "custom";
                            setInternalVariant(newVariant);
                            onVariantChange?.(newVariant);
                          } else {
                            // If onVariantChange is provided, toggle back to default, otherwise add color
                            if (onVariantChange) {
                              const newVariant = "default";
                              setInternalVariant(newVariant);
                              onVariantChange(newVariant);
                            } else {
                              onColorAdd?.(customColor);
                            }
                          }
                        }}
                        className="p-1.5 -ml-1.5 flex-shrink-0"
                      >
                        <Droplets className="size-3" />
                      </Button>
                    </div>
                  );
                } else {
                  // Add to existing row or create first row if no colors
                  const isFirstRowWithButton = currentColors.length === 0;

                  if (isFirstRowWithButton) {
                    rows.push(
                      <div key="droplets-row" className="flex items-center flex-shrink-0 h-7">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => {
                            if (currentVariant === "default") {
                              const newVariant = "custom";
                              setInternalVariant(newVariant);
                              onVariantChange?.(newVariant);
                            } else {
                              // If onVariantChange is provided, toggle back to default, otherwise add color
                              if (onVariantChange) {
                                const newVariant = "default";
                                setInternalVariant(newVariant);
                                onVariantChange(newVariant);
                              } else {
                                onColorAdd?.(customColor);
                              }
                            }
                          }}
                          className="p-1.5 flex-shrink-0"
                        >
                          <Droplets className="size-3" />
                        </Button>
                      </div>
                    );
                  } else {
                    // Update the last row to include the button with consistent spacing
                    const lastRowIndex = Math.floor((currentColors.length - 1) / colorsPerRow);

                    const isFirstRowReplacement = lastRowIndex === 0;
                    rows[rows.length - 1] = (
                      <div
                        key={`row-${lastRowIndex}`}
                        className={`flex items-center flex-shrink-0 h-7 ${!isFirstRowReplacement ? "mt-2" : ""}`}
                      >
                        <div className="flex gap-[20px] flex-shrink-0">
                          {currentColors
                            .slice(lastRowIndex * colorsPerRow, (lastRowIndex + 1) * colorsPerRow)
                            .map((color, index) => (
                              <ColorSelector
                                key={lastRowIndex * colorsPerRow + index}
                                color={color}
                                onClick={() => {
                                  onColorSelect?.(color);
                                  setCustomColor(color);
                                  setHsv(hexToHsv(color));
                                }}
                                className="flex-shrink-0"
                              />
                            ))}
                        </div>
                        <div className="ml-[14px]">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              if (currentVariant === "default") {
                                const newVariant = "custom";
                                setInternalVariant(newVariant);
                                onVariantChange?.(newVariant);
                              } else {
                                // If onVariantChange is provided, toggle back to default, otherwise add color
                                if (onVariantChange) {
                                  const newVariant = "default";
                                  setInternalVariant(newVariant);
                                  onVariantChange(newVariant);
                                } else {
                                  onColorAdd?.(customColor);
                                }
                              }
                            }}
                            className="p-1.5 flex-shrink-0"
                          >
                            <Droplets className="size-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  }
                }

                return rows;
              })()}
            </div>
          </div>
        </div>

        {/* Delete option */}
        <div className="border-t border-border p-1">
          <Button variant="ghost" size="sm" onClick={handleDeleteColor} className="w-full justify-start gap-2 h-8 px-2">
            <Trash2 className="size-4 text-muted-foreground" />
            <span className="text-sm">Delete</span>
          </Button>
        </div>
      </div>
    );
  }
);

ColorSelectorMenu.displayName = "ColorSelectorMenu";

export { ColorSelectorMenu };
